import { z } from 'zod'

export const MEMBERSHIP_PEOPLE_READ_CONTRACT_VERSION = '1.0.1' as const

const numericId = z.string().regex(/^[0-9]+$/)
const productKey = z.string().regex(/^[a-z][a-z0-9_-]{0,63}$/)
const nullableDate = z.string().datetime().nullable()

/** Query parsing stays permissive; legacy handlers retain validation and status precedence. */
export const membershipPeopleReadQuerySchema = z.object({}).passthrough()
export const membershipPeopleAuditReadQuerySchema = z.object({}).passthrough()

const productGrant = z.object({
  product: productKey,
  roleKey: z.string().min(1).max(120),
  roleDefinitionId: numericId,
}).passthrough()

const member = z.object({
  userId: numericId,
  role: z.enum(['org-admin', 'org-member']),
  status: z.enum(['active', 'suspended', 'removed']),
  productGrants: z.array(productGrant),
  joinedAt: nullableDate,
  lastSeenAt: nullableDate.optional(),
}).passthrough()

const invite = z.object({
  id: numericId,
  email: z.string().email().max(320),
  roleDefinitionId: numericId,
  product: productKey,
  status: z.enum(['pending', 'accepted', 'revoked', 'expired']),
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
}).passthrough()

const grant = z.object({
  id: numericId,
  userId: numericId,
  product: productKey,
  roleDefinitionId: numericId,
  createdAt: z.string().datetime(),
  revokedAt: nullableDate,
}).passthrough()

const auditEntry = z.object({
  id: numericId,
  occurredAt: z.string().datetime(),
  actorUserId: numericId.nullable(),
  entityType: z.string().min(1).max(128),
  entityId: z.string().max(160).nullable(),
  action: z.string().min(1).max(160),
  changedFields: z.array(z.record(z.unknown())),
}).passthrough()

export const membershipPeopleMembersResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_PEOPLE_READ_CONTRACT_VERSION),
  members: z.array(member),
  nextCursor: numericId.optional(),
}).passthrough()

export const membershipPeopleInvitesResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_PEOPLE_READ_CONTRACT_VERSION),
  invites: z.array(invite),
  nextCursor: numericId.optional(),
}).passthrough()

export const membershipPeopleGrantsResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_PEOPLE_READ_CONTRACT_VERSION),
  grants: z.array(grant),
  nextCursor: numericId.optional(),
}).passthrough()

export const membershipPeopleAuditResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_PEOPLE_READ_CONTRACT_VERSION),
  entries: z.array(auditEntry),
  nextCursor: numericId.optional(),
}).passthrough()

export type MembershipPeopleMember = z.infer<typeof member>
export type MembershipPeopleMembersResponse = z.infer<typeof membershipPeopleMembersResponseSchema>
export type MembershipPeopleInvite = z.infer<typeof invite>
export type MembershipPeopleInvitesResponse = z.infer<typeof membershipPeopleInvitesResponseSchema>
export type MembershipPeopleGrant = z.infer<typeof grant>
export type MembershipPeopleGrantsResponse = z.infer<typeof membershipPeopleGrantsResponseSchema>
export type MembershipPeopleAuditEntry = z.infer<typeof auditEntry>
export type MembershipPeopleAuditResponse = z.infer<typeof membershipPeopleAuditResponseSchema>
